import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Grid,
  Paper,
  Tooltip,
  TextField,
  MenuItem,
  Pagination,
  Tabs,
  Tab,
  ButtonGroup,
  Divider,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Badge,
} from '@mui/material';
import {
  Assignment,
  Visibility,
  Group,
  Person,
  CheckCircle,
  Info,
  Refresh,
  Download,
  Email,
  History,
  FilterList,
  Search,
  Clear,
  Send,
  ExpandMore,
  Business,
  Assessment,
  TableChart,
  PictureAsPdf,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { buildCertificateFilename, certificateHasMeaningfulWeight } from '../utils/certificateFilename';

interface Sample {
  id: string;
  sample_id: string;
  external_id?: string; // Client reference
  client: {
    company_name: string;
  };
  type_echantillon: string;
  priorite: string;
  date_fin_analyse: string;
  metadonnees?: any;
}

interface Certificate {
  id: string;
  certificate_number: string;
  certificate_type: 'INDIVIDUAL' | 'BATCH';
  status: 'DRAFT' | 'APPROVED' | 'ISSUED';
  generated_at: string;
  sample: Sample;
  samples?: Sample[]; // For batch certificates
}

const CertificatesNew: React.FC = () => {
  const { user } = useAuth();
  
  // State
  const [readySamples, setReadySamples] = useState<Sample[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Dialog states
  const [generateDialog, setGenerateDialog] = useState(false);
  const [selectedSamples, setSelectedSamples] = useState<Sample[]>([]);
  const [isBatchMode, setIsBatchMode] = useState(false);
  
  // New enhanced features state
  const [activeTab, setActiveTab] = useState(0); // 0: Generation, 1: History
  const [emailDialog, setEmailDialog] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    message: '',
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // History & Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCertificates, setTotalCertificates] = useState(0);
  
  // Generation Tab Pagination state
  const [generationCurrentPage, setGenerationCurrentPage] = useState(1);
  const [generationTotalPages, setGenerationTotalPages] = useState(1);
  const [totalReadySamples, setTotalReadySamples] = useState(0);
  const [generationPageSize] = useState(120); // Page size for generation tab (increased for better workflow)
  const [totalIssuedCertificates, setTotalIssuedCertificates] = useState(0);
  const [pageSize] = useState(10);
  
  // Filtering state
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    dateFrom: '',
    dateTo: '',
  });
  
  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Batch visualization state
  const [selectedClientGroups, setSelectedClientGroups] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grouped' | 'flat'>('grouped');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadReadySamples(),
        loadCertificates(),
        loadCertificateStats(),
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load data when filters or pagination change
  useEffect(() => {
    if (activeTab === 1) { // Only when on history tab
      loadCertificates();
    }
  }, [currentPage, filters, activeTab]);

  // Load data when generation tab pagination changes
  useEffect(() => {
    if (activeTab === 0) { // Only when on generation tab
      loadCertificates();
      loadReadySamples();
    }
  }, [currentPage, generationCurrentPage, activeTab]);

  const loadReadySamples = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Build query parameters for pagination
      const params = new URLSearchParams();
      params.append('page', generationCurrentPage.toString());
      params.append('limit', generationPageSize.toString());
      
      const url = `/api/v1/certificates/ready-samples?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReadySamples(data.data || []);
        
        // Update pagination info for generation tab
        if (data.pagination) {
          setGenerationTotalPages(data.pagination.totalPages || 1);
          setTotalReadySamples(data.pagination.total || 0);
        } else {
          // For basic mode, set simple pagination
          setGenerationTotalPages(1);
          setTotalReadySamples(data.data?.length || 0);
        }
      } else {
        throw new Error('Failed to load ready samples');
      }
    } catch (error) {
      console.error('Error loading ready samples:', error);
      throw error;
    }
  };

  const loadCertificates = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Build query parameters for pagination and filtering
      const params = new URLSearchParams();
      
      if (activeTab === 1) { // History tab with pagination
        params.append('page', currentPage.toString());
        params.append('limit', pageSize.toString());
        
        if (filters.search) params.append('search', filters.search);
        if (filters.status) params.append('status', filters.status);
        if (filters.type) params.append('type', filters.type);
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
      }
      
      const url = `/api/v1/certificates${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCertificates(data.data || []);
        
        // Update pagination info if available
        if (data.pagination) {
          setTotalPages(data.pagination.totalPages || 1);
          setTotalCertificates(data.pagination.total || 0);
        } else {
          // For basic mode, set simple pagination
          setTotalPages(1);
          setTotalCertificates(data.data?.length || 0);
        }
      } else {
        throw new Error('Failed to load certificates');
      }
    } catch (error) {
      console.error('Error loading certificates:', error);
      throw error;
    }
  };

  const loadCertificateStats = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Load total certificates count
      const totalResponse = await fetch('/api/v1/certificates?limit=1', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (totalResponse.ok) {
        const totalData = await totalResponse.json();
        if (totalData.pagination) {
          setTotalCertificates(totalData.pagination.total || 0);
        }
      }

      // Load issued certificates count
      const issuedResponse = await fetch('/api/v1/certificates?status=ISSUED&limit=1', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (issuedResponse.ok) {
        const issuedData = await issuedResponse.json();
        if (issuedData.pagination) {
          setTotalIssuedCertificates(issuedData.pagination.total || 0);
        }
      }
    } catch (error) {
      console.error('Error loading certificate stats:', error);
      // Don't throw here, just log the error
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleGenerateIndividual = (sample: Sample) => {
    setSelectedSamples([sample]);
    setIsBatchMode(false);
    setGenerateDialog(true);
  };

  const handleGenerateBatch = () => {
    if (readySamples.length === 0) {
      showSnackbar('Aucun échantillon disponible pour certificat de lot', 'warning');
      return;
    }
    
    // Enhanced grouping by client with better sample organization
    const samplesByClient = readySamples.reduce((acc, sample) => {
      const clientName = sample.client?.company_name || 'Unknown';
      if (!acc[clientName]) {
        acc[clientName] = {
          clientName,
          samples: [],
          totalSamples: 0
        };
      }
      acc[clientName].samples.push(sample);
      acc[clientName].totalSamples += 1;
      return acc;
    }, {} as Record<string, { clientName: string; samples: Sample[]; totalSamples: number }>);

    // Find clients with multiple samples eligible for batch certificates
    const eligibleClients = Object.values(samplesByClient).filter(client => client.totalSamples > 1);
    
    if (eligibleClients.length === 0) {
      showSnackbar('Aucun client avec plusieurs échantillons pour certificat de lot', 'warning');
      return;
    }

    // If multiple clients are eligible, use the first one, or let user choose
    if (eligibleClients.length > 1) {
      // For now, use the client with the most samples
      const clientWithMostSamples = eligibleClients.reduce((prev, current) => 
        current.totalSamples > prev.totalSamples ? current : prev
      );
      
      showSnackbar(`Certificat de lot proposé pour ${clientWithMostSamples.clientName} (${clientWithMostSamples.totalSamples} échantillons)`, 'info');
      setSelectedSamples(clientWithMostSamples.samples);
    } else {
      // Only one eligible client
      const eligibleClient = eligibleClients[0];
      setSelectedSamples(eligibleClient.samples);
    }
    
    setIsBatchMode(true);
    setGenerateDialog(true);
  };

  const handleConfirmGenerate = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('access_token');

      if (isBatchMode) {
        // Generate batch certificate
              const response = await fetch('/api/v1/certificates/generate/batch', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
          body: JSON.stringify({
            sampleIds: selectedSamples.map(s => s.id),
          }),
        });

        if (response.ok) {
          const result = await response.json();
          showSnackbar(`Certificat de lot généré: ${result.data.certificate_number}`, 'success');
          
          // Open certificate in new window
          setTimeout(() => {
            window.open(`/api/v1/certificates/${result.data.id}/download`, '_blank');
          }, 1000);
        } else {
          const error = await response.json();
          throw new Error(error.message || 'Erreur lors de la génération du certificat de lot');
        }
      } else {
        // Generate individual certificate
        const sample = selectedSamples[0];
        const response = await fetch(`/api/v1/certificates/generate/individual/${sample.id}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const result = await response.json();
          showSnackbar(`Certificat individuel généré: ${result.data.certificate_number}`, 'success');
          
          // Open certificate in new window
          setTimeout(() => {
            window.open(`/api/v1/certificates/${result.data.id}/download`, '_blank');
          }, 1000);
        } else {
          const error = await response.json();
          throw new Error(error.message || 'Erreur lors de la génération du certificat individuel');
        }
      }

      setGenerateDialog(false);
      setSelectedSamples([]);
      await loadData(); // Reload data to refresh the lists
    } catch (error) {
      console.error('Error generating certificate:', error);
      showSnackbar((error as Error).message || 'Erreur lors de la génération du certificat', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleViewCertificate = (certificate: Certificate) => {
    const token = localStorage.getItem('access_token');
    
    // Create a form to POST the request with authentication
    const form = document.createElement('form');
    form.method = 'GET';
    form.action = `/api/v1/certificates/${certificate.id}/download`;
    form.target = '_blank';
    
    // Add authorization header by using fetch and blob approach
    fetch(`/api/v1/certificates/${certificate.id}/download`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Erreur lors de la récupération du certificat');
      }
      return response.text();
    })
    .then(htmlContent => {
      // Open the HTML content in a new window
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(htmlContent);
        newWindow.document.close();
      }
    })
    .catch(error => {
      console.error('Error viewing certificate:', error);
      showSnackbar('Erreur lors de la visualisation du certificat', 'error');
    });
  };

  // New enhanced functions
  const handleDownloadCertificate = (certificate: Certificate) => {
    const link = document.createElement('a');
    link.href = `/api/v1/certificates/${certificate.id}/download`;
    link.download = `${buildCertificateFilename(certificate)}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSnackbar('Téléchargement du certificat démarré', 'success');
  };

  const handleEmailCertificate = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    setEmailData({
      to: certificate.sample?.client?.company_name ? '' : '',
      subject: `Certificat d'Analyse AFRILAB - ${certificate.certificate_number}`,
      message: `Bonjour,\n\nVeuillez trouver ci-joint le certificat d'analyse pour l'échantillon ${certificate.sample?.sample_id}.\n\nCordialement,\nLaboratoire AFRILAB`,
    });
    setEmailDialog(true);
  };

  const handleSendEmail = async () => {
    if (!selectedCertificate || !emailData.to.trim()) {
      showSnackbar('Veuillez remplir l\'adresse email du destinataire', 'warning');
      return;
    }

    setSendingEmail(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/v1/certificates/send-email', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          certificateId: selectedCertificate.id,
          to: emailData.to,
          subject: emailData.subject,
          message: emailData.message,
        }),
      });

      if (response.ok) {
        showSnackbar('Email envoyé avec succès', 'success');
        setEmailDialog(false);
        setSelectedCertificate(null);
        setEmailData({ to: '', subject: '', message: '' });
      } else {
        throw new Error('Erreur lors de l\'envoi de l\'email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      showSnackbar('Erreur lors de l\'envoi de l\'email', 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  // New Export Functions
  const handleExportToExcel = async (certificate: Certificate) => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Fetch certificate data
      const response = await fetch(`/api/v1/certificates/${certificate.id}/data`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des données du certificat');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Erreur lors de la récupération des données');
      }
      
      // Generate proper Excel file with professional layout
      await generateProfessionalExcel(result.data, certificate.certificate_number);
      
      showSnackbar('Export Excel réussi', 'success');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showSnackbar('Erreur lors de l\'export Excel', 'error');
    }
  };

  const handleExportToPDF = async (certificate: Certificate) => {
    try {
      const token = localStorage.getItem('access_token');
      showSnackbar('Génération du PDF en cours...', 'info');

      const response = await fetch(`/api/v1/certificates/${certificate.id}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la génération du PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${buildCertificateFilename(certificate)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSnackbar('PDF téléchargé avec succès', 'success');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      showSnackbar('Erreur lors de la génération du PDF', 'error');
    }
  };

  // Helper function to convert certificate data to CSV (REAL data like HTML table)
  const convertCertificateToCSV = (certificateData: any): string => {
    let csvContent = '';
    
    if (certificateData.certificate_type === 'BATCH' && certificateData.samples) {
      // BATCH CERTIFICATE - Table format like HTML
      const elements = certificateData.elements || [];
      
      // Header row: Lab-Ref, Client-Ref, then all elements
      const headers = ['Lab-Ref', 'Client-Ref', ...elements];
      csvContent = headers.map(h => `"${h}"`).join(',') + '\n';
      
      // Data rows: each sample with its results
      certificateData.samples.forEach((sample: any) => {
        const row = [
          `"${sample.lab_ref}"`,
          `"${sample.client_ref}"`,
          ...elements.map((element: string) => {
            const result = sample.results?.find((r: any) => r.element === element);
            return result ? result.concentration : '0';
          })
        ];
        csvContent += row.join(',') + '\n';
      });
    } else {
      // INDIVIDUAL CERTIFICATE - Element list format
      const headers = ['Élément', 'Concentration', 'Unité'];
      csvContent = headers.map(h => `"${h}"`).join(',') + '\n';
      
      const results = certificateData.results || [];
      results.forEach((result: any) => {
        const row = [
          `"${result.element}"`,
          `"${result.concentration}"`,
          `"${result.unit}"`
        ];
        csvContent += row.join(',') + '\n';
      });
    }
    
    return csvContent;
  };

  // Helper function to check if certificate has QA/QC data
  const hasQAQCData = (certificateData: any): boolean => {
    if (certificateData.certificate_type !== 'BATCH') return false;
    if (!certificateData.samples || certificateData.samples.length === 0) return false;
    
    const firstSample = certificateData.samples[0];
    const metadonnees = firstSample?.sample_data?.metadonnees || firstSample?.metadonnees;
    
    const qaqcData = metadonnees?.qaqc_data;
    const hasData = qaqcData?.reference_standards && 
                    Array.isArray(qaqcData.reference_standards) && 
                    qaqcData.reference_standards.length > 0;
    
    return !!hasData;
  };

  // Generate QA/QC worksheet for BGM certificates
  const generateQAQCWorksheet = (workbook: any, certificateData: any, ExcelJS: any) => {
    const firstSample = certificateData.samples[0];
    const metadonnees = firstSample?.sample_data?.metadonnees || firstSample?.metadonnees;
    const qaqcData = metadonnees?.qaqc_data;
    
    if (!qaqcData?.reference_standards) return;
    
    const qaqcSheet = workbook.addWorksheet('QAQC');
    
    // Dynamically detect elements from reference_standards data.
    // Zgounder element order: Ag, Cu, Pb, Fe, Zn (then others).
    const knownElements = ['Ag', 'Cu', 'Fe', 'Pb', 'Zn', 'Cd', 'Co', 'Mn', 'Ni', 'Ag_FA', 'Au_FA', 'Mo_ICP', 'As_ICP', 'Sn_ICP'];
    const detectedElements = new Set<string>();
    qaqcData.reference_standards.forEach((std: any) => {
      Object.keys(std).forEach((key: string) => {
        if (key !== 'customers_ref' && key !== 'lab_ref' && knownElements.includes(key)) {
          detectedElements.add(key);
        }
      });
    });
    const allElements = detectedElements.size > 0 
      ? knownElements.filter(el => detectedElements.has(el))
      : ['Ag', 'Cu', 'Fe', 'Pb', 'Zn', 'Ag_FA', 'Au_FA', 'Mo_ICP', 'As_ICP', 'Sn_ICP'];

    const elementUnitsMap: Record<string, string> = {
      'Ag': 'g/t', 'Cu': '%', 'Fe': '%', 'Pb': '%', 'Zn': '%',
      'Cd': '%', 'Co': '%', 'Mn': '%', 'Ni': '%',
      'Ag_FA': 'g/t', 'Au_FA': 'g/t',
      'Mo_ICP': 'ppm', 'As_ICP': 'ppm', 'Sn_ICP': 'ppm'
    };
    const elementUnits = elementUnitsMap;
    const methodMap: Record<string, string> = {
      'Ag': 'MO/ANA/002', 'Cu': 'MO/ANA/002', 'Fe': 'MO/ANA/002', 'Pb': 'MO/ANA/002', 'Zn': 'MO/ANA/002',
      'Cd': 'MO/ANA/002', 'Co': 'MO/ANA/002', 'Mn': 'MO/ANA/002', 'Ni': 'MO/ANA/002',
      'Ag_FA': 'MO/ANA/035', 'Au_FA': 'MO/ANA/001', 'Mo_ICP': 'MO/ANA/038', 'As_ICP': 'MO/ANA/038', 'Sn_ICP': 'MO/ANA/038'
    };
    const limitsMap: Record<string, string> = {
      'Ag': '1', 'Cu': '0,01', 'Fe': '0,01', 'Pb': '0,01', 'Zn': '0,01',
      'Cd': '0,01', 'Co': '0,01', 'Mn': '0,01', 'Ni': '0,01',
      'Ag_FA': '200', 'Au_FA': '0,05', 'Mo_ICP': '1', 'As_ICP': '1', 'Sn_ICP': '1'
    };
    const methodHeaders = allElements.map(el => methodMap[el] || 'MO/ANA/002');
    const limitHeaders = allElements.map(el => limitsMap[el] || '?');
    
    const headerStyle = {
      font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' }, name: 'Segoe UI' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB8CCE4' } },
      alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
      border: {
        top: { style: 'thin', color: { argb: 'FF555555' } },
        bottom: { style: 'thin', color: { argb: 'FF555555' } },
        left: { style: 'thin', color: { argb: 'FF555555' } },
        right: { style: 'thin', color: { argb: 'FF555555' } }
      }
    };
    
    const dataStyle = {
      font: { size: 10, name: 'Segoe UI' },
      alignment: { horizontal: 'center', vertical: 'middle' },
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } },
      border: {
        top: { style: 'hair', color: { argb: 'FFDDDDDD' } },
        bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } },
        left: { style: 'hair', color: { argb: 'FFDDDDDD' } },
        right: { style: 'hair', color: { argb: 'FFDDDDDD' } }
      }
    };
    
    const emptyDataStyle = {
      ...dataStyle,
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD3D3D3' } }
    };

    // Build lookup: lab_ref (sample_id) -> real external_id (customers_ref) for BIS samples
    const sampleExternalIdByLabRef: Record<string, string> = {};
    (certificateData.samples || []).forEach((s: any) => {
      const sid = s?.sample_id || s?.sample_data?.sample_id;
      const ext = s?.external_id || s?.sample_data?.external_id;
      if (sid) sampleExternalIdByLabRef[String(sid).trim()] = ext ? String(ext) : '';
    });

    // Unit-aware formatter for QA/QC values (mirrors main results table)
    const formatQaqcValue = (rawValue: any, element: string): string => {
      if (rawValue === null || rawValue === undefined) return '';
      const strVal = String(rawValue).trim();
      if (strVal === '') return '';
      const num = parseFloat(strVal.replace(',', '.'));
      if (Number.isNaN(num)) return strVal;
      const unit = elementUnits[element];
      if (unit === '%') {
        const pct = num / 10000;
        if (pct < 0.01) return '<0.01';
        return pct.toFixed(2);
      }
      if (element === 'Au_FA') {
        if (num < 0.05) return '<0.05';
        return num.toFixed(2);
      }
      if (element === 'Ag_FA') {
        if (num < 200) return '<200';
        return String(Math.round(num));
      }
      if (element === 'Ag') {
        if (num < 1) return '<1';
        return String(Math.round(num));
      }
      if (unit === 'ppm') {
        if (num < 1) return '<1';
        return String(Math.round(num));
      }
      return strVal;
    };

    const titleLastCol = String.fromCharCode(64 + allElements.length + 2);
    qaqcSheet.mergeCells(`A1:${titleLastCol}1`);
    qaqcSheet.getCell('A1').value = '2. QA/QC Results';
    qaqcSheet.getCell('A1').style = {
      font: { bold: true, size: 14, color: { argb: 'FF000000' }, name: 'Segoe UI' },
      alignment: { horizontal: 'left', vertical: 'middle' }
    };
    qaqcSheet.getRow(1).height = 25;
    
    qaqcSheet.mergeCells('A3:B3');
    qaqcSheet.getCell('A3').value = 'METHOD';
    qaqcSheet.getCell('A3').style = headerStyle;
    allElements.forEach((el, idx) => {
      const cell = qaqcSheet.getCell(3, idx + 3);
      cell.value = methodHeaders[idx];
      cell.style = headerStyle;
    });
    qaqcSheet.getRow(3).height = 25;
    
    qaqcSheet.mergeCells('A4:B4');
    qaqcSheet.getCell('A4').value = 'LIMITE DETECTION';
    qaqcSheet.getCell('A4').style = headerStyle;
    allElements.forEach((el, idx) => {
      const cell = qaqcSheet.getCell(4, idx + 3);
      cell.value = limitHeaders[idx];
      cell.style = headerStyle;
    });
    qaqcSheet.getRow(4).height = 25;
    
    qaqcSheet.getCell('A5').value = 'Customers-Ref';
    qaqcSheet.getCell('A5').style = headerStyle;
    qaqcSheet.getCell('B5').value = 'Lab-Ref';
    qaqcSheet.getCell('B5').style = headerStyle;
    allElements.forEach((el, idx) => {
      const cell = qaqcSheet.getCell(5, idx + 3);
      cell.value = `${el}\n${elementUnits[el]}`;
      cell.style = headerStyle;
    });
    qaqcSheet.getRow(5).height = 30;
    
    qaqcData.reference_standards.forEach((standard: any, rowIdx: number) => {
      const rowNum = 6 + rowIdx;

      const labRef = (standard.lab_ref || '').toString().trim();
      const resolvedExternalId = labRef ? sampleExternalIdByLabRef[labRef] : undefined;
      const displayCustomersRef = (labRef && resolvedExternalId !== undefined && resolvedExternalId !== '')
        ? resolvedExternalId
        : (standard.customers_ref || '');

      const custCell = qaqcSheet.getCell(rowNum, 1);
      custCell.value = displayCustomersRef || '';
      custCell.style = (!displayCustomersRef || displayCustomersRef.toString().trim() === '')
                       ? emptyDataStyle : dataStyle;

      const labCell = qaqcSheet.getCell(rowNum, 2);
      labCell.value = standard.lab_ref || '';
      labCell.style = (!standard.lab_ref || standard.lab_ref.trim() === '')
                      ? emptyDataStyle : dataStyle;

      allElements.forEach((el, colIdx) => {
        const cell = qaqcSheet.getCell(rowNum, colIdx + 3);
        const rawValue = standard[el];
        const formatted = formatQaqcValue(rawValue, el);
        cell.value = formatted;
        cell.style = (formatted === '') ? emptyDataStyle : dataStyle;
      });

      qaqcSheet.getRow(rowNum).height = 22;
    });
    
    const totalCols = allElements.length + 2;
    const lastColLetter = String.fromCharCode(64 + totalCols);
    const footerRow = 6 + qaqcData.reference_standards.length + 1;
    qaqcSheet.mergeCells(`A${footerRow}:${lastColLetter}${footerRow}`);
    const footerCell = qaqcSheet.getCell(`A${footerRow}`);
    const analystName = qaqcData.batch_info?.analyst_name || 'N/A';
    const analysisDate = qaqcData.batch_info?.analysis_date 
      ? new Date(qaqcData.batch_info.analysis_date).toLocaleDateString('en-GB') 
      : 'N/A';
    footerCell.value = `* QA/QC data validated by: ${analystName} on ${analysisDate}`;
    footerCell.style = {
      font: { italic: true, size: 9, name: 'Segoe UI' },
      alignment: { horizontal: 'left', vertical: 'middle' }
    };
    
    qaqcSheet.columns = [
      { width: 25 }, { width: 20 },
      ...allElements.map(() => ({ width: 12 }))
    ];
  };

  // 🏆 PREMIUM CORPORATE EXCEL: Ultra-Modern Professional Design with Advanced Styling & Data Extraction
  const generateProfessionalExcel = async (certificateData: any, certificateNumber: string) => {
    try {
      // Load ExcelJS library with full styling support
      if (!(window as any).ExcelJS) {
        await loadExcelJSLibrary();
      }

      const ExcelJS = (window as any).ExcelJS;
      
      // Create workbook with ExcelJS (full styling support)
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Rapport AA');
      
      // ===== EXTRACT REAL CLIENT DATA FROM METADATA =====

      
      // Extract client info from batch metadata or samples
      let clientInfo = {
        client_name: 'AFRILAB CLIENT',
        project_name: 'GEOLOGICAL ANALYSIS', 
        client_reference: 'SAMPLE ANALYSIS',
        order_number: '',
        receipt_date: new Date().toLocaleDateString('en-GB')
      };
      
      // Try to extract from batch metadata
      if (certificateData.batch_info) {
        const metadata = certificateData.batch_info;
        clientInfo = {
          client_name: metadata.client_name || metadata.clientName || clientInfo.client_name,
          project_name: metadata.project_name || metadata.projectName || metadata.project || clientInfo.project_name,
          client_reference: metadata.client_reference || metadata.clientReference || metadata.reference || clientInfo.client_reference,
          order_number: metadata.order_number || metadata.orderNumber || metadata.order || clientInfo.order_number,
          receipt_date: metadata.receipt_date || metadata.receiptDate || metadata.date_reception || clientInfo.receipt_date
        };
      }
      
      // Try to extract from samples metadata  
      if (certificateData.samples && certificateData.samples.length > 0) {
        const firstSample = certificateData.samples[0];
        if (firstSample.metadata || firstSample.batch_info) {
          const sampleMeta = firstSample.metadata || firstSample.batch_info;
          clientInfo = {
            client_name: sampleMeta.client_name || sampleMeta.clientName || clientInfo.client_name,
            project_name: sampleMeta.project_name || sampleMeta.projectName || sampleMeta.project || clientInfo.project_name,
            client_reference: sampleMeta.client_reference || sampleMeta.clientReference || sampleMeta.reference || clientInfo.client_reference,
            order_number: sampleMeta.order_number || sampleMeta.orderNumber || sampleMeta.order || clientInfo.order_number,
            receipt_date: sampleMeta.receipt_date || sampleMeta.receiptDate || sampleMeta.date_reception || clientInfo.receipt_date
          };
        }
      }
      

      
      // ===== PREMIUM CORPORATE STYLING (Based on 2024 Design Trends) =====
      
      const headerStyle = {
        font: { bold: true, size: 12, color: { argb: 'FFFFFFFF' }, name: 'Segoe UI' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4A90D9' } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true, indent: 1 },
        border: {
          top: { style: 'medium', color: { argb: 'FF2E5984' } },
          bottom: { style: 'medium', color: { argb: 'FF2E5984' } },
          left: { style: 'thin', color: { argb: 'FFAAAAAA' } },
          right: { style: 'thin', color: { argb: 'FFAAAAAA' } }
        }
      };
      
      const titleStyle = {
        font: { bold: true, size: 16, color: { argb: 'FF1F4E79' }, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
      };
      
      const companyStyle = {
        font: { bold: true, size: 22, color: { argb: 'FF0F2C47' }, name: 'Segoe UI' },
        alignment: { horizontal: 'left', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
      };
      
      const dataStyle = {
        font: { size: 11, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle', indent: 0.5 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } },
        border: {
          top: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          bottom: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          left: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          right: { style: 'hair', color: { argb: 'FFE1E5E9' } }
        }
      };
      
      const dataAlternateStyle = {
        font: { size: 11, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle', indent: 0.5 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } },
        border: {
          top: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          bottom: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          left: { style: 'hair', color: { argb: 'FFE1E5E9' } },
          right: { style: 'hair', color: { argb: 'FFE1E5E9' } }
        }
      };
      
      const subtitleStyle = {
        font: { bold: true, size: 14, color: { argb: 'FF2F5597' }, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } }
      };
      
      const clientHeaderStyle = {
        font: { bold: true, size: 11, color: { argb: 'FFFFFFFF' }, name: 'Segoe UI' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF6BAA75' } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true, indent: 1 },
        border: {
          top: { style: 'medium', color: { argb: 'FF4A7C59' } },
          bottom: { style: 'medium', color: { argb: 'FF4A7C59' } },
          left: { style: 'thin', color: { argb: 'FFAAAAAA' } },
          right: { style: 'thin', color: { argb: 'FFAAAAAA' } }
        }
      };
      
      // ===== BUILD BEAUTIFUL LAYOUT =====
      
      // Row 1: AFRILAB Company Header with Premium Corporate Style
      worksheet.mergeCells('A1:L1');
      const afrilab = worksheet.getCell('A1');
      afrilab.value = '✨ AFRILAB - African Laboratory for Mining and Environment ✨';
      afrilab.style = companyStyle;
      
      // Add subtle spacing row
      worksheet.getRow(2).height = 12;
      
      // Row 3: Report Form Title (moved down for better spacing)
      worksheet.mergeCells('D3:I3');
      const formTitle = worksheet.getCell('D3');
      formTitle.value = '🔬 FORMULAIRE REALISATION 🔬';
      formTitle.style = {
        font: { bold: true, size: 15, color: { argb: 'FF2F5597' }, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F4F8' } },
        border: {
          top: { style: 'thin', color: { argb: 'FFCBD5E0' } },
          bottom: { style: 'thin', color: { argb: 'FFCBD5E0' } },
          left: { style: 'thin', color: { argb: 'FFCBD5E0' } },
          right: { style: 'thin', color: { argb: 'FFCBD5E0' } }
        }
      };
      
      // Row 5-6: Report Titles (adjusted row numbers)
      worksheet.mergeCells('D5:I5');
      const reportTitle1 = worksheet.getCell('D5');
      reportTitle1.value = '📊 RAPPORT D\'ANALYSE 📊';
      reportTitle1.style = subtitleStyle;
      
      worksheet.mergeCells('D6:I6');
      const reportTitle2 = worksheet.getCell('D6');
      reportTitle2.value = '📈 ANALYTICAL REPORT 📈';
      reportTitle2.style = subtitleStyle;
      
      // Row 6: Certificate Number with Premium Design Box
      worksheet.mergeCells('J6:L6');
      const certCell = worksheet.getCell('J6');
      certCell.value = `🏆 Certificat N° ${certificateNumber} 🏆`;
      certCell.style = {
        font: { bold: true, size: 12, color: { argb: 'FF0F2C47' }, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFBBF24' } },
        border: {
          top: { style: 'thick', color: { argb: 'FFD97706' } },
          bottom: { style: 'thick', color: { argb: 'FFD97706' } },
          left: { style: 'thick', color: { argb: 'FFD97706' } },
          right: { style: 'thick', color: { argb: 'FFD97706' } }
        }
      };
      
      // Row 9-10: Report Info
      const dateCell = worksheet.getCell('A9');
      dateCell.value = 'Report issue date';
      dateCell.style = { font: { bold: true } };
      
      const dateCellValue = worksheet.getCell('C9');
      dateCellValue.value = new Date().toLocaleDateString('en-GB');
      
      const descCell = worksheet.getCell('A10');
      descCell.value = 'Please find below the results of analyzes.';
      
      // Build Zgounder report number: AM/QA/DM/RA/LZ02492/02/26/V01
      const firstSampleId = certificateData.samples?.[0]?.sample_data?.sample_id || 
                            certificateData.samples?.[0]?.sample_id || '';
      console.log('[CERT DEBUG] firstSampleId:', firstSampleId);
      console.log('[CERT DEBUG] certificateData.samples[0]:', JSON.stringify(certificateData.samples?.[0], null, 2)?.substring(0, 500));
      const lotMatch = firstSampleId.match(/(LZ)-?(\d+)-(\d+)/);
      console.log('[CERT DEBUG] lotMatch:', lotMatch);
      const lotCode = lotMatch ? lotMatch[1] + lotMatch[2] : certificateNumber;
      const lotYear = lotMatch ? lotMatch[3] : new Date().getFullYear().toString().slice(-2);
      const submissionMonth = String(new Date().getMonth() + 1).padStart(2, '0');
      const reportNumber = `AM/QA/DM/RA/${lotCode}/${submissionMonth}/${lotYear}/V01`;
      console.log('[CERT DEBUG] reportNumber:', reportNumber);
      
      // ===== CLIENT INFO TABLE - VERTICAL LAYOUT (Zgounder format) =====
      
      const infoLabelStyle = {
        font: { bold: true, size: 11, name: 'Segoe UI' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD6E4F0' } },
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF999999' } },
          bottom: { style: 'thin', color: { argb: 'FF999999' } },
          left: { style: 'thin', color: { argb: 'FF999999' } },
          right: { style: 'thin', color: { argb: 'FF999999' } }
        }
      };
      
      const infoValueStyle = {
        font: { size: 11, name: 'Segoe UI' },
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF999999' } },
          bottom: { style: 'thin', color: { argb: 'FF999999' } },
          left: { style: 'thin', color: { argb: 'FF999999' } },
          right: { style: 'thin', color: { argb: 'FF999999' } }
        }
      };
      
      const infoRows = [
        ['Report number', reportNumber],
        ['', ''],
        ['Company', clientInfo.client_name],
        ['Interlocutor', '-'],
        ['Client reference', clientInfo.client_reference],
        ['', ''],
        ['Order number', clientInfo.order_number || '-'],
        ['Imputation code', '-'],
        ['Date of receipt', clientInfo.receipt_date || '-'],
        ['Date of analysis', new Date().toLocaleDateString('en-GB')],
        ['Sampled by', 'Customer'],
        ['Warehouse', '-'],
        ['Prepared by', 'AFRILAB'],
      ];
      
      infoRows.forEach((row, i) => {
        const rowNum = 12 + i;
        worksheet.mergeCells(rowNum, 1, rowNum, 2);
        const labelCell = worksheet.getCell(rowNum, 1);
        labelCell.value = row[0];
        labelCell.style = infoLabelStyle;
        
        worksheet.mergeCells(rowNum, 3, rowNum, 6);
        const valueCell = worksheet.getCell(rowNum, 3);
        valueCell.value = row[1];
        valueCell.style = infoValueStyle;
      });
      
      // Comments row after info table
      const commentsRow = 12 + infoRows.length + 1;
      const commentsCell = worksheet.getCell(commentsRow, 1);
      commentsCell.value = 'Comments: R.A.S';
      commentsCell.style = { font: { bold: true, size: 11, color: { argb: 'FF2F5597' }, name: 'Segoe UI' } };
      
      // ===== ANALYSIS TABLE WITH PROFESSIONAL STYLING =====
      
      if (certificateData.certificate_type === 'BATCH' && certificateData.samples) {
        // Detect if this is a BGM batch
        const firstSample = certificateData.samples[0];
        const metadonnees = firstSample?.sample_data?.metadonnees || firstSample?.metadonnees;
        const isBGMBatch = metadonnees?.bgm_method_selected === true;

        // Hide LAB_WEIGHT column when no sample has a real (non-2.5) weight.
        const hasWeight = certificateHasMeaningfulWeight(certificateData);

        if (isBGMBatch) {
          // ===== BGM-SPECIFIC 25-COLUMN STRUCTURE =====
          
          // Define the fixed BGM column structure (25 columns: A-Y)
          const bgmColumnsAll = [
            // Basic info (A-C)
            { key: 'Customers-Ref', header: 'Customers-Ref', method: '', limit: '', unit: '' },
            { key: 'Lab-Ref', header: 'Lab-Ref', method: '', limit: '', unit: '' },
            { key: 'LAB_WEIGHT', header: 'LAB_WEIGHT', method: '', limit: '', unit: 'Kg' },
            
            // SAA Elements (D-H) - HAS VALUES
            { key: 'Ag', header: 'Ag ', method: 'MO/ANA/002', limit: '1', unit: ' g/t' },
            { key: 'Cu', header: 'Cu', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
            { key: 'Fe', header: 'Fe ', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
            { key: 'Pb', header: 'Pb ', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
            { key: 'Zn', header: 'Zn', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
            
            // SAA Check Elements (I-M) - EMPTY
            { key: 'Ag_CK', header: 'Ag_CK ', method: 'MO/ANA/002', limit: '1', unit: ' g/t' },
            { key: 'Cu_CK', header: 'Cu_CK', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
            { key: 'Fe_CK', header: 'Fe_CK', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
            { key: 'Pb_CK', header: 'Pb_CK', method: 'MO/ANA/002', limit: '0.01', unit: '%' },
            { key: 'Zn_CK', header: 'Zn_CK', method: 'MO/ANA/002', limit: '0.01', unit: ' %' },
            
            // Fire Assay Silver (N-O) - EMPTY
            { key: 'Ag_FA', header: 'Ag_FA', method: 'MO/ANA/035', limit: '200', unit: ' g/t' },
            { key: 'Ag_FA_CK', header: 'Ag_FA_CK', method: 'MO/ANA/035', limit: '200', unit: ' g/t' },
            
            // Fire Assay Gold (P) - HAS VALUES
            { key: 'Au', header: 'Au_FA', method: 'MO/ANA/001', limit: '0.05', unit: ' g/t' },
            
            // ICP Elements (Q-S) - HAS VALUES
            { key: 'Mo', header: 'Mo_ICP', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            { key: 'As', header: 'As_ICP', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            { key: 'Sn', header: 'Sn_ICP', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            
            // Gravimetric Gold (T-V) - EMPTY
            { key: 'Au_GRAV', header: 'Au_GRAV', method: 'MO/ANA/001', limit: '100', unit: ' g/t' },
            { key: 'Au_GRAV_CK', header: 'Au_GRAV_CK', method: 'MO/ANA/001', limit: '100', unit: ' g/t' },
            { key: 'Au_FA_CK', header: 'Au_FA_CK', method: 'MO/ANA/001', limit: '0.05', unit: ' g/t' },
            
            // ICP Check Elements (W-Y) - EMPTY
            { key: 'Mo_ICP_CK', header: 'Mo_ICP_CK', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            { key: 'As_ICP_CK', header: 'As_ICP_CK', method: 'MO/ANA/038', limit: '1', unit: 'ppm' },
            { key: 'Sn_ICP_CK', header: 'Sn_ICP_CK', method: 'MO/ANA/038', limit: '1', unit: 'ppm' }
          ];

          // Drop LAB_WEIGHT column when no sample has a real weight.
          const bgmColumns = hasWeight
            ? bgmColumnsAll
            : bgmColumnsAll.filter(c => c.key !== 'LAB_WEIGHT');
          const bgmBasicColCount = hasWeight ? 3 : 2;
          const bgmBasicMergeEnd = hasWeight ? 'C' : 'B';

          // Row 15: METHOD
          worksheet.mergeCells(`A15:${bgmBasicMergeEnd}15`);
          const methodCell = worksheet.getCell('A15');
          methodCell.value = 'METHOD';
          methodCell.style = headerStyle;
          
          bgmColumns.forEach((col, i) => {
            if (i >= bgmBasicColCount) {
              const cell = worksheet.getCell(15, i + 1);
              cell.value = col.method;
              cell.style = headerStyle;
            }
          });
          
          // Row 16: LIMITE DETECTION
          worksheet.mergeCells(`A16:${bgmBasicMergeEnd}16`);
          const limitsCell = worksheet.getCell('A16');
          limitsCell.value = 'LIMITE DETECTION';
          limitsCell.style = headerStyle;
          
          bgmColumns.forEach((col, i) => {
            if (i >= bgmBasicColCount) {
              const cell = worksheet.getCell(16, i + 1);
              cell.value = col.limit;
              cell.style = headerStyle;
            }
          });
          
          // Row 17: Headers
          bgmColumns.forEach((col, i) => {
            const cell = worksheet.getCell(17, i + 1);
            cell.value = col.header;
            cell.style = headerStyle;
          });
          
          // Row 18: Units
          bgmColumns.forEach((col, i) => {
            const cell = worksheet.getCell(18, i + 1);
            cell.value = col.unit;
            cell.style = headerStyle;
          });
          
          // Sample Data Rows
          certificateData.samples.forEach((sample: any, index: number) => {
            const rowNum = 19 + index;
            const isEvenRow = index % 2 === 0;
            const currentDataStyle = isEvenRow ? dataStyle : dataAlternateStyle;
            
            const receptionWeight = sample.sample_data?.metadonnees?.poids_kg || 
                                   sample.metadonnees?.poids_kg || 
                                   sample.weight || 0.5;
            
            // Build row data according to BGM structure
            const sampleData: any[] = [];
            
            bgmColumns.forEach((col) => {
              if (col.key === 'Customers-Ref') {
                sampleData.push(sample.sample_data?.external_id || sample.client_ref || sample.external_id || `D-${195 + index}`);
              } else if (col.key === 'Lab-Ref') {
                sampleData.push(sample.sample_data?.sample_id || sample.lab_ref || sample.sample_id || `AFL-${certificateNumber}-${String(index + 1).padStart(2, '0')}`);
              } else if (col.key === 'LAB_WEIGHT') {
                sampleData.push(parseFloat(receptionWeight).toFixed(4));
              } else if (col.key === 'Ag') {
                // 🎯 BGM FEATURE: Ag superior limit - show ">200" when value exceeds 200 g/t
                const result = sample.results?.find((r: any) => r.element === 'Ag');
                if (result) {
                  const concentration = parseFloat(result.concentration || 0);
                  if (concentration > 200) {
                    sampleData.push('>200'); // Superior limit exceeded
                  } else {
                    const formatted = result.formatted_concentration;
                    if (formatted !== undefined && formatted !== null) {
                      sampleData.push(String(formatted));
                    } else {
                      sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
                    }
                  }
                } else {
                  sampleData.push('');
                }
              } else if (col.key === 'Ag_FA') {
                // 🎯 BGM FEATURE: Ag_FA shows actual value ONLY when Ag > 200 (avoid redundancy)
                const agResult = sample.results?.find((r: any) => r.element === 'Ag');
                if (agResult) {
                  const concentration = parseFloat(agResult.concentration || 0);
                  if (concentration > 200) {
                    // Show actual Ag value (since Ag column shows ">200")
                    const formatted = agResult.formatted_concentration;
                    if (formatted !== undefined && formatted !== null) {
                      sampleData.push(String(formatted));
                    } else {
                      sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
                    }
                  } else {
                    // Empty cell (value already shown in Ag column)
                    sampleData.push('');
                  }
                } else {
                  sampleData.push('');
                }
              } else if (col.key.endsWith('_CK') || col.key === 'Au_GRAV' || col.key === 'Au_GRAV_CK' || col.key === 'Au_FA_CK') {
                // Empty check columns
                sampleData.push('');
              } else {
                // Data columns: Cu, Fe, Pb, Zn, Au, Mo, As, Sn
                const result = sample.results?.find((r: any) => r.element === col.key);
                if (result) {
                  const formatted = result.formatted_concentration;
                  if (formatted !== undefined && formatted !== null) {
                    sampleData.push(String(formatted));
                  } else {
                    const concentration = parseFloat(result.concentration || 0);
                    sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
                  }
                } else {
                  sampleData.push('');
                }
              }
            });
            
            worksheet.getRow(rowNum).height = 24;
            
            sampleData.forEach((value, i) => {
              const cell = worksheet.getCell(rowNum, i + 1);
              cell.value = value;
              cell.style = currentDataStyle;
            });
          });
          
        } else {
          // ===== NON-BGM BATCHES: ZGOUNDER DYNAMIC BEHAVIOR =====

          const elements: string[] = Array.isArray(certificateData.elements) ? certificateData.elements : [];
          const tableStartRow = commentsRow + 3;

          // LAB_WEIGHT column visibility (see certificateHasMeaningfulWeight above).
          const basicColCount = hasWeight ? 3 : 2;
          const elementColOffset = basicColCount + 1; // first element column (1-indexed)

          // "Please find below the results of analyses:" label
          const resultsLabelRow = tableStartRow - 1;
          worksheet.getCell(resultsLabelRow, 1).value = '1. Please find below the results of analyses:';
          worksheet.getCell(resultsLabelRow, 1).style = { font: { bold: true, size: 12, name: 'Segoe UI' } };

          // METHOD row
          worksheet.mergeCells(tableStartRow, 1, tableStartRow, basicColCount);
          const methodCell = worksheet.getCell(tableStartRow, 1);
          methodCell.value = 'METHOD';
          methodCell.style = headerStyle;
        
          const firstSample = certificateData.samples[0];
          elements.forEach((elementName: string, i) => {
            const cell = worksheet.getCell(tableStartRow, i + elementColOffset);
            const res = firstSample?.results?.find((r: any) => r.element === elementName);
            let code = res?.method_code || 'PRE.MO/ANA/002';
            code = code.replace(/^AA-/, '');
            cell.value = code;
            cell.style = headerStyle;
          });
        
          // LIMITE DETECTION row
          const limRow = tableStartRow + 1;
          worksheet.mergeCells(limRow, 1, limRow, basicColCount);
          const limitsCell = worksheet.getCell(limRow, 1);
          limitsCell.value = 'LIMITE DETECTION';
          limitsCell.style = headerStyle;
        
          elements.forEach((elementName: string, i) => {
            const cell = worksheet.getCell(limRow, i + elementColOffset);
            const res = firstSample?.results?.find((r: any) => r.element === elementName);
            const dl = res?.detection_limit;
            if (dl && typeof dl === 'object' && dl.limit !== undefined) {
              const unit = res?.unit || dl.unit || 'ppm';
              if (unit === 'g/t') {
                cell.value = dl.limit;
              } else {
                cell.value = dl.limitInPercent || (dl.limit / 10000) || 0.01;
              }
            } else if (typeof dl === 'number') {
              cell.value = dl;
            } else {
              cell.value = elementName === 'Ag' || elementName === 'Au' ? 1 : 0.01;
            }
            cell.style = headerStyle;
          });
        
          // Column Headers row (Customers-Ref, Lab-Ref, [LAB_WEIGHT], elements*)
          const hdrRow = tableStartRow + 2;
          const headers = hasWeight
            ? ['Customers-Ref', 'Lab-Ref', 'LAB_WEIGHT']
            : ['Customers-Ref', 'Lab-Ref'];
          elements.forEach((element: string) => headers.push(element + '*'));
        
          headers.forEach((header, i) => {
            const cell = worksheet.getCell(hdrRow, i + 1);
            cell.value = header;
            cell.style = headerStyle;
          });
        
          // Units row
          const unitRow = tableStartRow + 3;
          const units = hasWeight ? ['', '', 'Kg'] : ['', ''];
          elements.forEach((elementName: string) => {
            if (elementName === 'Ag' || elementName === 'Au') {
              units.push('g/t');
            } else {
              units.push('%');
            }
          });
        
          units.forEach((unit, i) => {
            const cell = worksheet.getCell(unitRow, i + 1);
            cell.value = unit;
            cell.style = headerStyle;
          });
        
          // Sample Data Rows
          const dataStartRow = tableStartRow + 4;
          certificateData.samples.forEach((sample: any, index: number) => {
            const rowNum = dataStartRow + index;
            const isEvenRow = index % 2 === 0;
            const currentDataStyle = isEvenRow ? dataStyle : dataAlternateStyle;
            
            // 🔧 CRITICAL FIX: Use actual reception weight from metadonnees (exactly like HTML)
            const receptionWeight = sample.sample_data?.metadonnees?.poids_kg || 
                                   sample.metadonnees?.poids_kg || 
                                   sample.weight || 0.5;

            const sampleData: any[] = [
              sample.sample_data?.external_id || sample.client_ref || sample.external_id || `D-${195 + index}`,
              sample.sample_data?.sample_id || sample.lab_ref || sample.sample_id || `AFL-${certificateNumber}-${String(index + 1).padStart(2, '0')}`,
            ];
            if (hasWeight) {
              sampleData.push(parseFloat(receptionWeight).toFixed(4));
            }
            
            // 🔧 FIX: Get this sample's selected elements to check if element should show "-"
            const sampleSelectedElements = sample.sample_data?.metadonnees?.selected_elements || 
                                           sample.metadonnees?.selected_elements || [];
            
            elements.forEach((element: string) => {
              // 🔧 FIX: Check if element is in this sample's selected_elements
              const hasElement = sampleSelectedElements.includes(element);
              
              if (!hasElement) {
                // Element not selected for this sample - show "-"
                sampleData.push('-');
                return;
              }
              
              const result = sample.results?.find((r: any) => r.element === element);
              if (result) {
                // Prefer backend-provided formatted value which already applies method-specific detection limits
                const formatted = result.formatted_concentration;
                if (formatted !== undefined && formatted !== null) {
                  sampleData.push(String(formatted));
                } else {
                  // Fallback to raw concentration with minimal formatting
                  const concentration = parseFloat(result.concentration || 0);
                  sampleData.push(Number.isFinite(concentration) ? concentration.toFixed(2) : 'N/A');
                }
              } else {
                // No result found - show a generic below-detection indicator
                sampleData.push('');
              }
            });
            
            // Apply premium styling with generous row height
            worksheet.getRow(rowNum).height = 24; // Much taller data rows
            
            sampleData.forEach((value, i) => {
              const cell = worksheet.getCell(rowNum, i + 1);
              cell.value = value;
              cell.style = currentDataStyle;
            });
          });
        }
      } else if (certificateData.certificate_type === 'INDIVIDUAL') {
        const elements: string[] = Array.isArray(certificateData.elements) ? certificateData.elements : [];
        const results: any[] = Array.isArray(certificateData.results) ? certificateData.results : [];

        // LAB_WEIGHT column visibility: hide when single sample weight is missing or 2.5.
        const hasWeightIndiv = certificateHasMeaningfulWeight(certificateData);
        const indivBasicColCount = hasWeightIndiv ? 3 : 2;
        const indivBasicMergeEnd = hasWeightIndiv ? 'C' : 'B';
        const indivElementColOffset = indivBasicColCount + 1;

        // Method row
        worksheet.mergeCells(`A15:${indivBasicMergeEnd}15`);
        const methodCell = worksheet.getCell('A15');
        methodCell.value = 'METHOD';
        methodCell.style = headerStyle;
        elements.forEach((el, i) => {
          const cell = worksheet.getCell(15, i + indivElementColOffset);
          const res = results.find(r => r.element === el);
          cell.value = res?.method_code || '';
          cell.style = headerStyle;
        });

        // Detection limits row
        worksheet.mergeCells(`A16:${indivBasicMergeEnd}16`);
        const limitsCell = worksheet.getCell('A16');
        limitsCell.value = 'LIMITE DETECTION';
        limitsCell.style = headerStyle;
        elements.forEach((el, i) => {
          const cell = worksheet.getCell(16, i + indivElementColOffset);
          const res = results.find(r => r.element === el);
          const limit = res?.detection_limit;
          cell.value = typeof limit === 'number' ? limit : '';
          cell.style = headerStyle;
        });

        // Column headers
        const headers = hasWeightIndiv
          ? ['Customers-Ref', 'Lab-Ref', 'LAB_WEIGHT']
          : ['Customers-Ref', 'Lab-Ref'];
        elements.forEach((el: string) => headers.push(el + '*'));
        headers.forEach((header, i) => {
          const cell = worksheet.getCell(17, i + 1);
          cell.value = header;
          cell.style = headerStyle;
        });

        // Units row
        const units = hasWeightIndiv ? ['', '', 'Kg'] : ['', ''];
        elements.forEach((el: string) => {
          const res = results.find(r => r.element === el);
          units.push(res?.unit || '');
        });
        units.forEach((unit, i) => {
          const cell = worksheet.getCell(18, i + 1);
          cell.value = unit;
          cell.style = headerStyle;
        });

        // Data row
        const rowNum = 19;
        worksheet.getRow(rowNum).height = 24;
        const sampleRow: any[] = [
          certificateData.client_ref || 'N/A',
          certificateData.lab_ref || 'N/A',
        ];
        if (hasWeightIndiv) {
          sampleRow.push((certificateData.sample_data?.metadonnees?.poids_kg || 0).toFixed(4));
        }
        elements.forEach((el: string) => {
          const res = results.find(r => r.element === el);
          if (res) {
            const formatted = res.formatted_concentration;
            sampleRow.push(formatted != null ? String(formatted) : (Number.isFinite(+res.concentration) ? Number(res.concentration).toFixed(2) : 'N/A'));
          } else {
            sampleRow.push('');
          }
        });
        sampleRow.forEach((value, i) => {
          const cell = worksheet.getCell(rowNum, i + 1);
          cell.value = value as any;
          cell.style = dataStyle;
        });
      }
      
      // ===== PREMIUM SPACING & LAYOUT (Extra Wide & Tall) =====
      
      worksheet.columns = [
        { width: 25 }, // A: Customer-Ref (much wider for premium feel)
        { width: 22 }, // B: Lab-Ref (much wider for premium feel)
        { width: 15 }, // C: LAB_WEIGHT (wider)
        { width: 12 }, // D: Analysis columns (more spacious)
        { width: 12 }, // E: Analysis columns (more spacious)
        { width: 12 }, // F: Analysis columns (more spacious)
        { width: 12 }, // G: Analysis columns (more spacious)
        { width: 12 }, // H: Analysis columns (more spacious)
        { width: 12 }, // I: Analysis columns (more spacious)
        { width: 12 }, // J: Analysis columns (more spacious)
        { width: 12 }, // K: Analysis columns (more spacious)
        { width: 25 }  // L: Certificate column (much wider)
      ];
      
      // Set premium row heights with generous spacing
      worksheet.getRow(1).height = 45;  // AFRILAB header - extra prominent
      worksheet.getRow(2).height = 35;  // Form title - spacious
      worksheet.getRow(3).height = 20;  // More spacing
      worksheet.getRow(4).height = 30;  // Report titles - taller
      worksheet.getRow(5).height = 30;  // Report titles - taller
      worksheet.getRow(6).height = 28;  // Certificate number - premium height
      worksheet.getRow(7).height = 20;  // More spacing
      worksheet.getRow(8).height = 20;  // More spacing
      worksheet.getRow(9).height = 25;  // Date - taller
      worksheet.getRow(10).height = 25; // Description - taller
      worksheet.getRow(11).height = 20; // More spacing
      worksheet.getRow(12).height = 32; // Client headers - much taller
      worksheet.getRow(13).height = 28; // Client data - taller
      // Row heights are set dynamically based on tableStartRow in the results section
      
      // ===== ADD QA/QC WORKSHEET IF DATA EXISTS (BGM CERTIFICATES ONLY) =====
      
      if (hasQAQCData(certificateData)) {
        generateQAQCWorksheet(workbook, certificateData, ExcelJS);
      }
      
      // ===== ADD ANNEX WORKSHEET: REFERENCE OF METHODS =====
      
      const annexSheet = workbook.addWorksheet('Annexe');
      
      annexSheet.columns = [
        { width: 5 },
        { width: 25 },
        { width: 55 },
      ];
      
      const annexHeaderStyle = {
        font: { bold: true, size: 14, color: { argb: 'FF2F5597' }, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle' },
      };
      
      const annexTableHeaderStyle = {
        font: { bold: true, size: 12, color: { argb: 'FF000000' }, name: 'Segoe UI' },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD6E4F0' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF999999' } },
          bottom: { style: 'thin', color: { argb: 'FF999999' } },
          left: { style: 'thin', color: { argb: 'FF999999' } },
          right: { style: 'thin', color: { argb: 'FF999999' } },
        },
      };
      
      const annexCellStyle = {
        font: { size: 11, name: 'Segoe UI' },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin', color: { argb: 'FF999999' } },
          bottom: { style: 'thin', color: { argb: 'FF999999' } },
          left: { style: 'thin', color: { argb: 'FF999999' } },
          right: { style: 'thin', color: { argb: 'FF999999' } },
        },
      };
      
      annexSheet.mergeCells('A2:C2');
      annexSheet.getCell('A2').value = '2. ANNEX:';
      annexSheet.getCell('A2').style = { font: { bold: true, size: 13, underline: true, name: 'Segoe UI' } };
      
      annexSheet.mergeCells('A4:C4');
      annexSheet.getCell('A4').value = 'REFERENCE OF METHODS';
      annexSheet.getCell('A4').style = { font: { bold: true, size: 13, underline: true, name: 'Segoe UI' }, alignment: { horizontal: 'center' } };
      
      annexSheet.getCell('B6').value = 'Method';
      annexSheet.getCell('B6').style = annexTableHeaderStyle;
      annexSheet.getCell('C6').value = 'Description';
      annexSheet.getCell('C6').style = annexTableHeaderStyle;
      
      annexSheet.getCell('B7').value = 'PRE.MO/ANA/015';
      annexSheet.getCell('B7').style = { ...annexCellStyle, font: { bold: true, size: 11, name: 'Segoe UI' } };
      annexSheet.getCell('C7').value = 'Mechanical preparation';
      annexSheet.getCell('C7').style = annexCellStyle;
      
      annexSheet.getCell('B8').value = 'PRE.MO/ANA/002';
      annexSheet.getCell('B8').style = { ...annexCellStyle, font: { bold: true, size: 11, name: 'Segoe UI' } };
      annexSheet.getCell('C8').value = 'Ag, Cu, Fe, Pb and Zn by AAS Analysis';
      annexSheet.getCell('C8').style = annexCellStyle;
      
      annexSheet.getRow(6).height = 30;
      annexSheet.getRow(7).height = 30;
      annexSheet.getRow(8).height = 30;
      
      // ===== GENERATE AND DOWNLOAD PREMIUM CORPORATE EXCEL =====
      
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${buildCertificateFilename({ ...certificateData, certificate_number: certificateNumber })}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      
      
    } catch (error) {
      console.error('Excel generation error:', error);
      throw new Error('Erreur lors de la génération du fichier Excel professionnel');
    }
  };

  // Load ExcelJS library for professional Excel generation with full styling
  const loadExcelJSLibrary = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if ((window as any).ExcelJS) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load ExcelJS library'));
      document.head.appendChild(script);
    });
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      type: '',
      dateFrom: '',
      dateTo: '',
    });
    setCurrentPage(1);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENTE': return 'error';
      case 'ELEVEE': return 'warning';
      default: return 'primary';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ISSUED': return 'success';
      case 'APPROVED': return 'info';
      case 'DRAFT': return 'warning';
      default: return 'default';
    }
  };

  // Batch visualization helper functions
  const groupSamplesByBatch = (samples: Sample[]) => {
    const batches: Record<string, Sample[]> = {};
    const individualSamples: Sample[] = [];

    samples.forEach(sample => {
      const batchInfo = sample.metadonnees?.batch_info;
      if (batchInfo && batchInfo.is_batch_sample && batchInfo.batch_number) {
        if (!batches[batchInfo.batch_number]) {
          batches[batchInfo.batch_number] = [];
        }
        batches[batchInfo.batch_number].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    // Sort batches by batch number and individual samples by sample ID
    Object.keys(batches).forEach(batchNumber => {
      batches[batchNumber].sort((a, b) => {
        const posA = a.metadonnees?.batch_info?.sample_position || 0;
        const posB = b.metadonnees?.batch_info?.sample_position || 0;
        return posA - posB;
      });
    });

    return { batches, individualSamples };
  };

  const isBatchSample = (sample: Sample): boolean => {
    return sample.metadonnees?.batch_info?.is_batch_sample === true;
  };

  const getBatchNumber = (sample: Sample): string | null => {
    return sample.metadonnees?.batch_info?.batch_number || null;
  };

  const getBatchPosition = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.sample_position || 1;
  };

  const getTotalSamplesInBatch = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.total_samples || 1;
  };

  const handleBatchSelection = (batchNumber: string, isSelected: boolean) => {
    const newSelection = new Set(selectedClientGroups);
    if (isSelected) {
      newSelection.add(batchNumber);
    } else {
      newSelection.delete(batchNumber);
    }
    setSelectedClientGroups(newSelection);
  };

  const handleBatchExpansion = (batchNumber: string, isExpanded: boolean) => {
    const newExpanded = new Set(expandedGroups);
    if (isExpanded) {
      newExpanded.add(batchNumber);
    } else {
      newExpanded.delete(batchNumber);
    }
    setExpandedGroups(newExpanded);
  };

  const handleGenerateBatchFromLot = (batchNumber: string) => {
    const { batches } = groupSamplesByBatch(readySamples);
    const batchSamples = batches[batchNumber];
    
    if (!batchSamples || batchSamples.length === 0) {
      showSnackbar('Lot non trouvé', 'error');
      return;
    }

    // 🔥 FIX: Single-sample lots should use INDIVIDUAL certificate format
    // Multi-sample lots use BATCH certificate format (transposed table)
    if (batchSamples.length === 1) {
      // Generate individual certificate for single sample
      setSelectedSamples(batchSamples);
      setIsBatchMode(false); // Individual certificate
      setGenerateDialog(true);
    } else {
      // Generate batch certificate for multiple samples
      setSelectedSamples(batchSamples);
      setIsBatchMode(true); // Batch certificate
      setGenerateDialog(true);
    }
  };

  const handleGenerateBatchFromSelectedLots = () => {
    if (selectedClientGroups.size === 0) {
      showSnackbar('Veuillez sélectionner au moins un lot', 'warning');
      return;
    }

    // Get all samples from selected lots
    const { batches } = groupSamplesByBatch(readySamples);
    const selectedSamples = Array.from(selectedClientGroups)
      .filter(batchNumber => batches[batchNumber])
      .flatMap(batchNumber => batches[batchNumber]);

    if (selectedSamples.length === 0) {
      showSnackbar('Aucun échantillon trouvé dans les lots sélectionnés', 'warning');
      return;
    }

    setSelectedSamples(selectedSamples);
    setIsBatchMode(true);
    setGenerateDialog(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Gestion des Certificats
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadData}
          disabled={loading}
        >
          Actualiser
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          aria-label="certificates tabs"
        >
          <Tab 
            icon={<Assignment />} 
            label="Génération" 
            id="tab-0"
            aria-controls="tabpanel-0"
          />
          <Tab 
            icon={<History />} 
            label="Historique" 
            id="tab-1"
            aria-controls="tabpanel-1"
          />
        </Tabs>
      </Box>

      {/* Tab Panel 0: Generation */}
      {activeTab === 0 && (
        <Box>
          {/* Statistics */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Assignment color="primary" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {totalReadySamples}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Prêts pour Certificat
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircle color="success" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {totalCertificates}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Certificats Générés
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Info color="info" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {totalIssuedCertificates}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Certificats Émis
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Ready Samples Section - Batch Visualization */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  Échantillons Prêts pour Certificat ({readySamples.length} / {totalReadySamples})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <ButtonGroup variant="outlined" size="small">
                    <Button
                      variant={viewMode === 'grouped' ? 'contained' : 'outlined'}
                      startIcon={<Business />}
                      onClick={() => setViewMode('grouped')}
                    >
                      Vue Groupée
                    </Button>
                    <Button
                      variant={viewMode === 'flat' ? 'contained' : 'outlined'}
                      startIcon={<Assessment />}
                      onClick={() => setViewMode('flat')}
                    >
                      Vue Liste
                    </Button>
                  </ButtonGroup>
                  <Button
                    variant="contained"
                    startIcon={<Group />}
                    onClick={handleGenerateBatchFromSelectedLots}
                    disabled={selectedClientGroups.size === 0}
                  >
                    Certificat de Lot ({selectedClientGroups.size} lots)
                  </Button>
                </Box>
              </Box>

              {readySamples.length === 0 ? (
                <Alert severity="info">
                  Aucun échantillon prêt pour la génération de certificat.
                  Les échantillons doivent être validés avant la génération de certificat.
                </Alert>
              ) : (
                <>
                  {viewMode === 'grouped' ? (
                    // Grouped View - Lot-based accordions
                    <Box>
                      {(() => {
                        const { batches, individualSamples } = groupSamplesByBatch(readySamples);
                        const batchEntries = Object.entries(batches);
                        
                        return (
                          <>
                            {/* Batch Lots */}
                            {batchEntries.map(([batchNumber, batchSamples]) => (
                              <Accordion
                                key={batchNumber}
                                expanded={expandedGroups.has(batchNumber)}
                                onChange={(event, isExpanded) => 
                                  handleBatchExpansion(batchNumber, isExpanded)
                                }
                                sx={{ 
                                  mb: 1,
                                  background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
                                  border: '1px solid #2196f3',
                                  borderLeft: '6px solid #2196f3'
                                }}
                              >
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={selectedClientGroups.has(batchNumber)}
                                          onChange={(e) => handleBatchSelection(batchNumber, e.target.checked)}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      }
                                      label=""
                                      sx={{ mr: 1 }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <Assessment color="primary" />
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
                                      LOT {batchNumber}
                                    </Typography>
                                    <Badge 
                                      badgeContent={batchSamples.length} 
                                      color="primary"
                                      sx={{ mr: 2 }}
                                    >
                                      <Group />
                                    </Badge>
                                    <Chip 
                                      label={`${batchSamples.length} échantillons`}
                                      color="primary"
                                      size="small"
                                      sx={{ mr: 2 }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={batchSamples.length === 1 ? <Person /> : <Group />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleGenerateBatchFromLot(batchNumber);
                                        }}
                                        disabled={batchSamples.length === 0}
                                      >
                                        {batchSamples.length === 1 ? 'Certificat' : 'Certificat Lot'}
                                      </Button>
                                    </Box>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Échantillon</TableCell>
                                          <TableCell>Position</TableCell>
                                          <TableCell>Référence Client</TableCell>
                                          <TableCell>Client</TableCell>
                                          <TableCell>Type</TableCell>
                                          <TableCell>Priorité</TableCell>
                                          <TableCell>Date Analyse</TableCell>
                                          <TableCell>Actions</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {batchSamples.map((sample) => (
                                          <TableRow key={sample.id}>
                                            <TableCell>
                                              <Typography fontWeight="bold" variant="body2">
                                                {sample.sample_id}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Chip
                                                label={`${getBatchPosition(sample)}/${getTotalSamplesInBatch(sample)}`}
                                                color="info"
                                                size="small"
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2" color="primary">
                                                {sample.external_id || 'N/A'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {sample.client?.company_name || 'N/A'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {sample.type_echantillon}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Chip
                                                label={sample.priorite}
                                                color={getPriorityColor(sample.priorite)}
                                                size="small"
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {sample.date_fin_analyse 
                                                  ? new Date(sample.date_fin_analyse).toLocaleDateString('fr-FR')
                                                  : 'N/A'
                                                }
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Tooltip title="Générer certificat individuel">
                                                <IconButton
                                                  color="primary"
                                                  size="small"
                                                  onClick={() => handleGenerateIndividual(sample)}
                                                >
                                                  <Person />
                                                </IconButton>
                                              </Tooltip>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </AccordionDetails>
                              </Accordion>
                            ))}
                            
                            {/* Individual Samples */}
                            {individualSamples.length > 0 && (
                              <Accordion
                                expanded={expandedGroups.has('individual')}
                                onChange={(event, isExpanded) => 
                                  handleBatchExpansion('individual', isExpanded)
                                }
                                sx={{ 
                                  mb: 1,
                                  background: 'linear-gradient(135deg, #f1f8e9 0%, #e8f5e8 100%)',
                                  border: '1px solid #4caf50',
                                  borderLeft: '6px solid #4caf50'
                                }}
                              >
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    <Person color="success" />
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
                                      Échantillons Individuels
                                    </Typography>
                                    <Badge 
                                      badgeContent={individualSamples.length} 
                                      color="success"
                                      sx={{ mr: 2 }}
                                    >
                                      <Person />
                                    </Badge>
                                    <Chip 
                                      label={`${individualSamples.length} échantillons`}
                                      color="success"
                                      size="small"
                                    />
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Échantillon</TableCell>
                                          <TableCell>Référence Client</TableCell>
                                          <TableCell>Client</TableCell>
                                          <TableCell>Type</TableCell>
                                          <TableCell>Priorité</TableCell>
                                          <TableCell>Date Analyse</TableCell>
                                          <TableCell>Actions</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {individualSamples.map((sample) => (
                                          <TableRow key={sample.id}>
                                            <TableCell>
                                              <Typography fontWeight="bold" variant="body2">
                                                {sample.sample_id}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2" color="primary">
                                                {sample.external_id || 'N/A'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {sample.client?.company_name || 'N/A'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {sample.type_echantillon}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Chip
                                                label={sample.priorite}
                                                color={getPriorityColor(sample.priorite)}
                                                size="small"
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2">
                                                {sample.date_fin_analyse 
                                                  ? new Date(sample.date_fin_analyse).toLocaleDateString('fr-FR')
                                                  : 'N/A'
                                                }
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Tooltip title="Générer certificat individuel">
                                                <IconButton
                                                  color="primary"
                                                  size="small"
                                                  onClick={() => handleGenerateIndividual(sample)}
                                                >
                                                  <Person />
                                                </IconButton>
                                              </Tooltip>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </AccordionDetails>
                              </Accordion>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  ) : (
                    // Flat View - Traditional table
                    <>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Échantillon</TableCell>
                              <TableCell>Référence Client</TableCell>
                              <TableCell>Client</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Priorité</TableCell>
                              <TableCell>Date Analyse</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {readySamples.map((sample) => (
                              <TableRow key={sample.id}>
                                <TableCell>
                                  <Typography fontWeight="bold">
                                    {sample.sample_id}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="primary">
                                    {sample.external_id || 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                                <TableCell>{sample.type_echantillon}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={sample.priorite}
                                    color={getPriorityColor(sample.priorite)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {sample.date_fin_analyse 
                                    ? new Date(sample.date_fin_analyse).toLocaleDateString('fr-FR')
                                    : 'N/A'
                                  }
                                </TableCell>
                                <TableCell>
                                  <Tooltip title="Générer certificat individuel">
                                    <IconButton
                                      color="primary"
                                      onClick={() => handleGenerateIndividual(sample)}
                                    >
                                      <Person />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      {/* Pagination for Generation Tab */}
                      {generationTotalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                          <Pagination
                            count={generationTotalPages}
                            page={generationCurrentPage}
                            onChange={(_, page) => setGenerationCurrentPage(page)}
                            color="primary"
                            showFirstButton
                            showLastButton
                          />
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Generated Certificates Section */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Certificats Générés ({certificates.length} / {totalCertificates})
              </Typography>

              {certificates.length === 0 ? (
                <Alert severity="info">
                  Aucun certificat généré pour le moment.
                </Alert>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Numéro</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Échantillon</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell>Date Génération</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {certificates.map((certificate) => (
                          <TableRow key={certificate.id}>
                            <TableCell>
                              <Typography fontWeight="bold">
                                {certificate.certificate_number}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={certificate.certificate_type}
                                color={certificate.certificate_type === 'BATCH' ? 'secondary' : 'primary'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{certificate.sample?.sample_id || 'N/A'}</TableCell>
                            <TableCell>{certificate.sample?.client?.company_name || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={certificate.status}
                                color={getStatusColor(certificate.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(certificate.generated_at).toLocaleDateString('fr-FR')}
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Voir certificat">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleViewCertificate(certificate)}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Pagination for Generated Certificates in Generation Tab */}
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => setCurrentPage(page)}
                        color="primary"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Tab Panel 1: History */}
      {activeTab === 1 && (
        <Box>
          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  <FilterList sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Filtres de Recherche
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={handleClearFilters}
                  size="small"
                >
                  Effacer
                </Button>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Recherche"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    placeholder="Numéro, échantillon, client..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Statut</InputLabel>
                    <Select
                      value={filters.status}
                      label="Statut"
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <MenuItem value="">Tous</MenuItem>
                      <MenuItem value="DRAFT">Brouillon</MenuItem>
                      <MenuItem value="APPROVED">Approuvé</MenuItem>
                      <MenuItem value="ISSUED">Émis</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={filters.type}
                      label="Type"
                      onChange={(e) => handleFilterChange('type', e.target.value)}
                    >
                      <MenuItem value="">Tous</MenuItem>
                      <MenuItem value="INDIVIDUAL">Individuel</MenuItem>
                      <MenuItem value="BATCH">Lot</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Date de début"
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Date de fin"
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* History Results */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  Historique des Certificats ({totalCertificates})
                </Typography>
                {loading && <CircularProgress size={24} />}
              </Box>

              {certificates.length === 0 ? (
                <Alert severity="info">
                  Aucun certificat trouvé avec les critères actuels.
                </Alert>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Numéro</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Échantillon</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell>Date Génération</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {certificates.map((certificate) => (
                          <TableRow key={certificate.id}>
                            <TableCell>
                              <Typography fontWeight="bold">
                                {certificate.certificate_number}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={certificate.certificate_type}
                                color={certificate.certificate_type === 'BATCH' ? 'secondary' : 'primary'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{certificate.sample?.sample_id || 'N/A'}</TableCell>
                            <TableCell>{certificate.sample?.client?.company_name || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={certificate.status}
                                color={getStatusColor(certificate.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(certificate.generated_at).toLocaleDateString('fr-FR')}
                            </TableCell>
                            <TableCell>
                              <ButtonGroup size="small" variant="outlined">
                                <Tooltip title="Voir certificat">
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleViewCertificate(certificate)}
                                  >
                                    <Visibility />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Télécharger HTML">
                                  <IconButton
                                    color="secondary"
                                    onClick={() => handleDownloadCertificate(certificate)}
                                  >
                                    <Download />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Export Excel">
                                  <IconButton
                                    color="success"
                                    onClick={() => handleExportToExcel(certificate)}
                                  >
                                    <TableChart />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Télécharger PDF">
                                  <IconButton
                                    color="warning"
                                    onClick={() => handleExportToPDF(certificate)}
                                  >
                                    <PictureAsPdf />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Envoyer par email">
                                  <IconButton
                                    color="info"
                                    onClick={() => handleEmailCertificate(certificate)}
                                  >
                                    <Email />
                                  </IconButton>
                                </Tooltip>
                              </ButtonGroup>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => setCurrentPage(page)}
                        color="primary"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Generate Certificate Dialog */}
      <Dialog open={generateDialog} onClose={() => setGenerateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isBatchMode ? <Group color="primary" /> : <Person color="primary" />}
            {isBatchMode ? 'Générer Certificat de Lot' : 'Générer Certificat Individuel'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {isBatchMode 
              ? `Vous allez générer un certificat de lot pour ${selectedSamples.length} échantillons:`
              : 'Vous allez générer un certificat individuel pour l\'échantillon:'
            }
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            {selectedSamples.map((sample) => (
              <Paper key={sample.id} sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography fontWeight="bold">{sample.sample_id}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Type: {sample.type_echantillon}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="primary">
                      Réf. Client: {sample.external_id || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Client: {sample.client?.company_name || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>

          {isBatchMode && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Les certificats de lot ne peuvent être générés que pour des échantillons du même client.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setGenerateDialog(false)}
            disabled={generating}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmGenerate}
            disabled={generating}
            startIcon={generating ? null : <Assignment />}
          >
            {generating ? 'Génération...' : 'Générer Certificat'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Certificate Dialog */}
      <Dialog 
        open={emailDialog} 
        onClose={() => setEmailDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Email color="primary" />
            Envoyer le Certificat par Email
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedCertificate && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                Certificat: <strong>{selectedCertificate.certificate_number}</strong><br />
                Échantillon: <strong>{selectedCertificate.sample?.sample_id}</strong><br />
                Client: <strong>{selectedCertificate.sample?.client?.company_name}</strong>
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Destinataire *"
                    type="email"
                    value={emailData.to}
                    onChange={(e) => setEmailData(prev => ({ ...prev, to: e.target.value }))}
                    placeholder="client@example.com"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Objet"
                    value={emailData.subject}
                    onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    multiline
                    rows={4}
                    value={emailData.message}
                    onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Votre message personnalisé..."
                  />
                </Grid>
              </Grid>

              <Alert severity="warning" sx={{ mt: 2 }}>
                Le certificat sera joint au format HTML. Le destinataire pourra l'imprimer ou le sauvegarder en PDF.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEmailDialog(false)}
            disabled={sendingEmail}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={handleSendEmail}
            disabled={sendingEmail || !emailData.to.trim()}
            startIcon={sendingEmail ? <CircularProgress size={20} /> : <Send />}
          >
            {sendingEmail ? 'Envoi...' : 'Envoyer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CertificatesNew; 